import React, { useMemo, useCallback, useEffect, useState } from 'react';
import useVisitStore from '../../stores/visitStore';
import { usePermissions, useScope, useRoleView } from '../../context/PermissionContext';
import { useTheme } from '../../theme/ThemeContext';
import { sortByQueue } from '../../utils/queueSort';
import DoctorView from './DoctorQueueView';
import NurseView from './NurseTaskCards';
import AdminView from './AdminSummary';
import ReceptionistView from './ReceptionistDashboard';
import { WidgetShell, EmptyState } from './WorkflowShared';
import DoctorProceduresWidget from '../procedures/widget/DoctorProceduresWidget';
import LabQueueView from '../lab/LabQueueView';

/**
 * =======================================================================
 * SmartWorkflowWidget - Role-aware live CTAs on the Home Page
 *
 * Thin orchestrator that selects the correct role-specific view.
 * Each view is a self-contained component in its own file:
 *   - DoctorQueueView.jsx    (~960 lines)
 *   - NurseTaskCards.jsx      (~300 lines)
 *   - AdminSummary.jsx        (~300 lines)
 *   - ReceptionistDashboard.jsx (~800 lines)
 *   - WorkflowShared.jsx     (shared utilities & dialogs)
 * =======================================================================
 */

export default function SmartWorkflowWidget({ onNavigateToVisit, onOpenRegister, onNavigateToAppointments }) {
  const { role, hasPermission } = usePermissions();
  const { isScoped, memberId } = useScope();
  const { isDark } = useTheme();
  const { dailyVisits, updateVisit, loading, consultationStats, loadConsultationStats, skipVisit, requeueVisit, doctors, fetchDoctors, updateDoctorAvailability, getDoctorAvailability, fetchDailyVisits } = useVisitStore();
  const viewKey = useRoleView();

  // Load doctors list (needed for availability toggles & badges)
  useEffect(() => {
    if (!doctors || doctors.length === 0) fetchDoctors();
  }, [doctors, fetchDoctors]);

  const roleUpper = (role || '').toUpperCase();

  // The PENDING pill is `position: fixed` on the right edge. The dashboard
  // header is `position: sticky; top: 0; z-index: 100`, so a hardcoded top
  // (96px) gets covered by the header when scrolled to the top. Measure the
  // header and anchor the pill just below it, re-measuring when its height
  // changes (theme/connection-status row, viewport resize). Falls back to 96
  // when no header is present (non-dashboard mounts).
  const [badgeAnchorPx, setBadgeAnchorPx] = useState(96);
  useEffect(() => {
    const header = document.querySelector('.admin-header');
    if (!header || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () =>
      setBadgeAnchorPx(Math.round(header.getBoundingClientRect().height + 12));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(header);
    return () => ro.disconnect();
  }, []);

  // CRITICAL: Doctor segregation - client-side safety net
  // Even if the store has visits from all doctors (race condition on
  // first load, or realtime events from other doctors), scoped roles
  // (DOCTOR) must only see their own visits. This is defense-in-depth
  // on top of the server-side scope.doctorId filter.
  //
  // Phase 1b: also include visits where the caller is the active
  // procedure handler — junior doctors / nurses temporarily own the
  // visit while executing a delegated sitting.
  //
  // Phase 6.0 / 10: also include visits surfaced via the co-worker
  // projection rows. The backend stamps `is_co_worker_visit=true` on
  // these when fetched through the per-doctor lane (GSI2). They carry
  // the co-worker's lane queue_number, so HAPPY_CLINIC sort downstream
  // treats them as a fresh entry in the co-worker's queue.
  const scopedDailyVisits = useMemo(() => {
    if (!isScoped || !memberId) return dailyVisits || [];
    return (dailyVisits || []).filter(
      (v) =>
        v.doctor_member_id === memberId
        || v.visit_doctor_id === memberId
        || v.active_handler_member_id === memberId
        || v.is_co_worker_visit === true
    );
  }, [dailyVisits, isScoped, memberId]);

  // Derive active & queued visits
  const { inProgressVisits, queueVisits, skippedVisits, allActiveVisits } = useMemo(() => {
    const active = (scopedDailyVisits).filter(
      (v) => v.status === 'IN_PROGRESS'
    );
    const queued = sortByQueue(
      (scopedDailyVisits).filter((v) => v.status === 'QUEUE')
    );
    const skipped = (scopedDailyVisits).filter(
      (v) => v.status === 'SKIPPED'
    );
    return {
      inProgressVisits: active,
      queueVisits: queued,
      skippedVisits: skipped,
      allActiveVisits: [...active, ...queued],
    };
  }, [scopedDailyVisits]);

  // Unique doctors for Nurse filter
  const uniqueDoctors = useMemo(() => {
    const map = {};
    (scopedDailyVisits).forEach((v) => {
      const id = v.doctor_member_id || v.visit_doctor_id;
      const name = v.doctor_name;
      if (id && name && !map[id]) map[id] = name;
    });
    return Object.entries(map).map(([id, name]) => ({ id, name }));
  }, [scopedDailyVisits]);

  // Safety-net: periodic fetch when doctor is idle
  // Catches edge cases where WS + reconciliation + cross-tab all miss
  // a new visit. Fetches every 30s when queue is empty (idle state),
  // or every 120s when actively consulting (just in case).
  useEffect(() => {
    const isIdle = queueVisits.length === 0 && inProgressVisits.length === 0;
    const intervalMs = isIdle ? 30_000 : 120_000;

    const timer = setInterval(() => {
      if (!document.hidden) {
        fetchDailyVisits();
      }
    }, intervalMs);

    return () => clearInterval(timer);
  }, [queueVisits.length, inProgressVisits.length, fetchDailyVisits]);

  // Load consultation stats (for views that need prediction)
  useEffect(() => {
    if (hasPermission('CREATE_VISIT') || hasPermission('MANAGE_QUEUE')) {
      loadConsultationStats();
    }
  }, [hasPermission, loadConsultationStats]);

  // Start consultation handler (Doctor) - stays on homepage
  const handleStartConsultation = useCallback(
    async (visitId) => {
      await updateVisit(visitId, { status: 'IN_PROGRESS' });
      // No navigation - DoctorView will auto-show the inline workflow
    },
    [updateVisit]
  );

  // Guard: only show if user has relevant permissions
  if (!hasPermission('VIEW_QUEUE') && !hasPermission('VIEW_VISIT_DASHBOARD')) {
    return null;
  }

  // Lab technician — their dashboard IS the lab queue (independent of
  // visits). S8 — no "⚡ Smart Workflow" title row here: the S6 status cards
  // are this screen's own header, and this isn't a sub-widget of a bigger
  // workflow the way every other role's tile is.
  if (viewKey === 'LabTechView') {
    return (
      <WidgetShell isDark={isDark} hideHeader>
        <LabQueueView />
      </WidgetShell>
    );
  }

  // No visits at all -> soft empty state
  // Doctors skip this — DoctorQueueView has its own STATE 3 (empty)
  // with the Rx Practice Tour nudge banner.
  if ((!scopedDailyVisits || scopedDailyVisits.length === 0) && viewKey !== 'DoctorView') {
    return (
      <WidgetShell isDark={isDark}>
        <EmptyState
          isDark={isDark}
          icon="🌟"
          title="No visits yet today"
          subtitle={
            hasPermission('CREATE_VISIT') && !hasPermission('ADD_VITALS')
              ? 'Register the first patient to get started'
              : 'Visits will appear here as patients arrive'
          }
          action={
            hasPermission('CREATE_VISIT') && !hasPermission('ADD_VITALS') && onOpenRegister
              ? { label: '+ Register Visit', onClick: onOpenRegister }
              : null
          }
        />
      </WidgetShell>
    );
  }

  return (
    <WidgetShell isDark={isDark}>
      {viewKey === 'DoctorView' && (
        <DoctorView
          inProgress={inProgressVisits}
          queue={queueVisits}
          dailyVisits={scopedDailyVisits}
          isDark={isDark}
          loading={loading.update}
          onStart={handleStartConsultation}
          onNavigate={onNavigateToVisit}
          updateVisit={updateVisit}
          role={roleUpper}
          skipVisit={skipVisit}
          memberId={memberId}
          doctorAvailability={memberId ? getDoctorAvailability(memberId) : 'available'}
          updateDoctorAvailability={updateDoctorAvailability}
        />
      )}

      {viewKey === 'NurseView' && (
        <NurseView
          activeVisits={allActiveVisits}
          uniqueDoctors={uniqueDoctors}
          doctors={doctors}
          isDark={isDark}
          onNavigate={onNavigateToVisit}
          updateVisit={updateVisit}
          role={roleUpper}
          skipVisit={skipVisit}
          memberId={memberId}
        />
      )}

      {/* Phase 4.0e — single consolidated "Procedures" pill for the
          doctor (replaces the 4.0d two-pill layout). Hover-opens to a
          tabbed panel with "My tasks" + "Delegated" axes. The per-visit
          disc inside DoctorQueueView (top:50%) is unchanged and serves
          live consultation context only — these two together give the
          doctor the right edge they want without alignment confusion. */}
      {viewKey === 'DoctorView' && memberId && (
        <DoctorProceduresWidget
          viewer={{ memberId, role: roleUpper }}
          verticalAnchorPx={badgeAnchorPx}
        />
      )}

      {viewKey === 'AdminView' && (
        <AdminView
          inProgress={inProgressVisits}
          queue={queueVisits}
          dailyVisits={scopedDailyVisits}
          doctors={doctors}
          isDark={isDark}
          onNavigate={onNavigateToVisit}
          onOpenRegister={onOpenRegister}
        />
      )}

      {viewKey === 'ReceptionistView' && (
        <ReceptionistView
          queue={queueVisits}
          inProgress={inProgressVisits}
          skipped={skippedVisits}
          dailyVisits={scopedDailyVisits}
          consultationStats={consultationStats}
          uniqueDoctors={uniqueDoctors}
          doctors={doctors}
          isDark={isDark}
          onOpenRegister={onOpenRegister}
          onNavigate={onNavigateToVisit}
          onNavigateToAppointments={onNavigateToAppointments}
          updateVisit={updateVisit}
          skipVisit={skipVisit}
          requeueVisit={requeueVisit}
          role={roleUpper}
        />
      )}
    </WidgetShell>
  );
}